import { useState } from "react";
import { Form } from "@/components/shared/forms/Form";
import { useForm, SubmitHandler } from "react-hook-form";
import { useParams, useRouteContext } from "@tanstack/react-router";
import {
  type GridRowClickEvent,
  type GridColumnProps,
} from "@progress/kendo-react-grid";

import { clenMutationFunctions } from "@/models/chld";
import { type CLEN, CLEN_TEMPLATE } from "@/models.ts";
import { useLeaData } from "@/queries.ts";
import KendoTableClientState from "@/components/shared/tables/KendoTableClientState";
import TooltipTrigger from "@/components/shared/TooltipTrigger";
import Popup from "@/components/shared/Popup";
import CLENAddEditForm from "../CLENAddEditForm";
import { type ComboBoxOption } from "@/components/shared/forms/SearchComboBox";
import { FiEdit, FiEye, FiPlus, FiSave, FiTrash2 } from "react-icons/fi";
import {
  createNamedFilename,
  CSVDownloadButton,
} from "@/components/shared/CSVDownloadButton";
import { useRecordSubmission } from "@/hooks/useRecordSubmission";
import LoaderButton from "@/components/shared/LoaderButton";
import { DeleteConfirmModal } from "@/components/shared/DeleteConfirmModal";

const columns: GridColumnProps[] = [
  {
    id: "ProgramYear",
    field: "ProgramYear",
    title: "Program Year",
  },
  {
    id: "PreschoolName",
    field: "PreschoolName",
    title: "School",
  },
  {
    id: "LocalClassroomId",
    field: "LocalClassroomId",
    title: "Classroom ID",
  },
  {
    id: "ChildName",
    field: "ChildName",
    title: "Child Name",
  },
];

const csvHeaders = [
  { label: "Program Year", key: "ProgramYear" },
  { label: "School", key: "PreschoolName" },
  { label: "Classroom ID", key: "LocalClassroomId" },
  { label: "Child Name", key: "ChildName" },
];

export default function CLENRecordsTab({
  clenRecords,
  classroomName,
}: {
  clenRecords: CLEN[];
  classroomName?: string;
}) {
  const params = useParams({
    shouldThrow: false,
    from: "/$leaid/_error-wrapper/records/classroom/$entityid",
  });
  const leaContext = useRouteContext({ from: "/$leaid" });
  const [popupOpen, setPopupOpen] = useState<boolean>(false);
  const [popupCLEN, setPopupCLEN] = useState<CLEN | undefined>(undefined);
  const [clenDefaults, setClenDefaults] = useState<Partial<CLEN> | undefined>(
    undefined
  );
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);

  const addEditForm = useForm<CLEN>({
    defaultValues: {},
  });

  const result = useLeaData(leaContext.currentUserOrg.esiIdPath);
  const leaData = result.data;

  const clrmRecords = params?.entityid
    ? (leaData?.entities?.classrooms?.[params.entityid]?.records?.map(
        (record) => ({
          ...record,
          PreschoolName: leaContext?.currentOrgPreschools?.find(
            (x) => x.PreschoolCDSCode === record.PreschoolCode
          )?.PreschoolName,
        })
      ) ?? [])
    : [];

  clrmRecords.sort((x, y) => y.ProgramYear.localeCompare(x.ProgramYear));
  const currentClassroom = clrmRecords[0];

  const childOptions: ComboBoxOption[] =
    leaData?.chld && currentClassroom
      ? leaData.chld
          .filter(
            (child) =>
              child.LocalChildId &&
              child.PreschoolCode === currentClassroom.PreschoolCode &&
              child.ProgramYear === currentClassroom.ProgramYear &&
              !child.EffectiveEndDate
          )
          .map((child) => ({
            value: child.LocalChildId,
            label: [child.ChildFirstName, child.ChildLastName]
              .filter(Boolean)
              .join(" "),
            sublabel: `CICN: ${child.LocalChildId}`,
            extra: { CSPPID: child.CSPPID ?? "" },
          }))
          .sort((a, b) => a.label.localeCompare(b.label))
      : [];
  const closePopup = () => {
    setPopupOpen(false);
    setPopupCLEN(undefined);
    setClenDefaults(undefined);
    setIsDeleting(false);
    setShowDeleteConfirm(false);
    addEditForm.reset({});
    addEditForm.clearErrors();
  };

  const { isSubmitting, setSubmitResult, handleSubmitStart } =
    useRecordSubmission({
      form: addEditForm,
      recordType: "CLEN",
      toastMessage:
        "Classroom enrollment record submitted.\nWaiting for validation...",
      onSuccess: closePopup,
    });

  const {
    isSubmitting: isDeleteSubmitting,
    setSubmitResult: setDeleteSubmitResult,
    handleSubmitStart: handleDeleteStart,
  } = useRecordSubmission({
    form: addEditForm,
    recordType: "CLEN",
    toastMessage: "Classroom enrollment record submitted for deletion",
    successMessage: "Classroom enrollment record deleted successfully",
    onSuccess: closePopup,
  });

  const mutateFns = clenMutationFunctions(leaData, setSubmitResult);
  const deleteMutateFns = clenMutationFunctions(leaData, setDeleteSubmitResult);

  const onSubmit: SubmitHandler<CLEN> = (x) => {
    handleSubmitStart();
    mutateFns.handleSave(x);
  };

  const onDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    setShowDeleteConfirm(false);
    if (popupCLEN) {
      setIsDeleting(true);
      handleDeleteStart();
      deleteMutateFns.handleSave(popupCLEN, true);
    }
  };

  const onAddButtonClick = () => {
    const defaults: Partial<CLEN> = {
      PreschoolCode: currentClassroom?.PreschoolCode ?? "",
      ProgramYear: currentClassroom?.ProgramYear ?? "",
      LocalClassroomId: currentClassroom?.LocalClassroomId ?? "",
    };

    setClenDefaults(defaults);
    setPopupCLEN(undefined);
    setPopupOpen(true);

    addEditForm.reset({ ...CLEN_TEMPLATE, ...defaults });
    addEditForm.clearErrors();
  };

  const onEditButtonClick = (c: CLEN) => {
    setClenDefaults(undefined);
    setPopupCLEN(c);
    setPopupOpen(true);

    addEditForm.reset(c);
    addEditForm.clearErrors();
  };

  const formId = "frmCLEN";
  const canEdit = leaContext.hasRole("CLENEdit");

  const tableData = clenRecords.map((record) => ({
    ...record,
    ClassroomName: classroomName ?? currentClassroom?.ClassroomName ?? "",
  }));

  return (
    <>
      <div className="d-flex justify-content-end align-items-center mb-3">
        <TooltipTrigger tooltipText="Export classroom enrollment records">
          <CSVDownloadButton
            data={tableData}
            headers={csvHeaders}
            filename={createNamedFilename(
              ["ClassroomName"],
              "classroom-enrollment-record"
            )}
          />
        </TooltipTrigger>
        {canEdit && (
          <TooltipTrigger tooltipText="Add a classroom enrollment record">
            <button
              type="button"
              className="btn btn-primary"
              onClick={onAddButtonClick}
            >
              <FiPlus className="me-1" />
              Add
            </button>
          </TooltipTrigger>
        )}
      </div>

      <KendoTableClientState
        data={tableData}
        columns={columns}
        onRowClick={(event: GridRowClickEvent) => {
          onEditButtonClick(event.dataItem);
        }}
      />

      <Popup
        show={popupOpen}
        enforceFocus={!showDeleteConfirm}
        header={
          popupCLEN == null ? (
            <div
              className="d-flex align-items-center gap-2"
              style={{ fontSize: "1.25rem" }}
            >
              {canEdit ? <FiPlus size={20} /> : <FiEye size={20} />}
              <span>
                {canEdit
                  ? "Add Classroom Enrollment Record"
                  : "View Classroom Enrollment Record"}
              </span>
            </div>
          ) : (
            <div
              className="d-flex align-items-center gap-2"
              style={{ fontSize: "1.25rem" }}
            >
              {canEdit ? <FiEdit size={20} /> : <FiEye size={20} />}
              <span>
                {canEdit
                  ? "Edit Classroom Enrollment Record"
                  : "View Classroom Enrollment Record"}
              </span>
            </div>
          )
        }
        hasButton={false}
        submitFormId={canEdit ? formId : undefined}
        submitButtonText={
          popupCLEN ? (
            <span className="d-flex align-items-center">
              <FiEdit className="me-1" />
              Update
            </span>
          ) : (
            <span className="d-flex align-items-center">
              <FiSave className="me-1" />
              Save
            </span>
          )
        }
        submitButtonLoading={isSubmitting}
        submitButtonLoadingText="Submitting..."
        secondButtonText="Cancel"
        size="lg"
        onClose={closePopup}
        onSecondButtonClick={closePopup}
        canEdit={canEdit}
        leftFooterButton={
          canEdit && popupCLEN ? (
            <LoaderButton
              variant="danger"
              type="button"
              onClick={onDeleteClick}
              loading={isDeleting && isDeleteSubmitting}
              loadingText="Deleting..."
              className="d-flex align-items-center"
            >
              <FiTrash2 className="me-1" />
              Delete
            </LoaderButton>
          ) : undefined
        }
      >
        <Form id={formId} onSubmit={addEditForm.handleSubmit(onSubmit)}>
          <CLENAddEditForm
            selectedCLEN={popupCLEN}
            form={addEditForm}
            clenDefaults={clenDefaults}
            childOptions={childOptions}
          />
        </Form>
      </Popup>

      {showDeleteConfirm && (
        <DeleteConfirmModal
          onCancel={() => setShowDeleteConfirm(false)}
          onConfirm={handleConfirmDelete}
        />
      )}
    </>
  );
}
